import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { isAxiosError } from 'axios';
import { Button, Card, CardContent, CardHeader, CardTitle, Input, toast } from '@mlm/ui';
import { STOCKIST_CONSIGNMENT_PERMS, ACCOUNTING_PAYMENT_METHODS } from '@mlm/shared';
import {
  createStockistReceipt,
  createStockistReturn,
  getStockistSoa,
  listPosFundsAccounts,
  listStockistHoldings,
  listStockistInvoices,
  listStockistReceipts,
  voidStockistDoc,
} from '../../lib/api';
import { useActiveBranch } from '../../lib/use-branch';
import { printStockistSoa } from '../../lib/stockist-print';
import { useHasPerm } from '../../lib/auth-store';

const peso = (v: string | number) =>
  `₱${Number(v).toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function apiError(e: unknown): string {
  const code = isAxiosError(e)
    ? ((e.response?.data as { error?: string } | undefined)?.error ?? 'request_failed')
    : 'request_failed';
  switch (code.split(':')[0]) {
    case 'insufficient_consigned_stock':
      return 'They no longer hold that many units.';
    case 'no_open_invoices':
      return 'Nothing is outstanding for this stockist.';
    case 'allocation_exceeds_outstanding':
      return 'That is more than they owe. Enter the outstanding amount or less.';
    case 'invoice_already_paid':
      return 'That billing is already settled.';
    case 'funds_account_required':
      return 'Pick which account received the money.';
    case 'branch_required':
      return 'A cash payment needs a till branch.';
    case 'already_void':
      return 'That document is already void.';
    case 'stockist_consignment_partially_consumed':
      return 'Some of those units have already sold through — that note can no longer be voided.';
    default:
      return 'Request failed.';
  }
}

/**
 * One stockist: what they hold, what they owe, and the two things a cashier
 * does about it — take back unsold stock, and receive payment.
 *
 * There is deliberately NO "create billing" action. A stockist's receivable is
 * raised by their own sale on our POS, at the price it was consigned at; typing
 * one by hand would be a second, competing source of truth.
 */
export function StockistBranchDetailPage() {
  const { branchId: stockistBranchId } = useParams<{ branchId: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { branchId: tillBranchId, isScoped } = useActiveBranch();
  const canReceive = useHasPerm(STOCKIST_CONSIGNMENT_PERMS.receivePayment);
  const canReturn = useHasPerm(STOCKIST_CONSIGNMENT_PERMS.return);
  const canVoid = useHasPerm(STOCKIST_CONSIGNMENT_PERMS.void);
  const canPrint = useHasPerm(STOCKIST_CONSIGNMENT_PERMS.print);

  const soaQ = useQuery({
    queryKey: ['stockist-soa', stockistBranchId],
    queryFn: () => getStockistSoa(stockistBranchId!),
    enabled: !!stockistBranchId,
  });
  const holdingsQ = useQuery({
    queryKey: ['stockist-holdings', stockistBranchId],
    queryFn: () => listStockistHoldings(stockistBranchId!),
    enabled: !!stockistBranchId,
  });
  const invoicesQ = useQuery({
    queryKey: ['stockist-invoices', stockistBranchId],
    queryFn: () => listStockistInvoices(stockistBranchId!),
    enabled: !!stockistBranchId,
  });
  const receiptsQ = useQuery({
    queryKey: ['stockist-receipts', stockistBranchId],
    queryFn: () => listStockistReceipts(stockistBranchId!),
    enabled: !!stockistBranchId,
  });
  const fundsQ = useQuery({
    queryKey: ['pos-funds-accounts', tillBranchId],
    queryFn: () => listPosFundsAccounts(tillBranchId),
  });

  const refresh = () => {
    for (const k of [
      'stockist-soa',
      'stockist-holdings',
      'stockist-invoices',
      'stockist-receipts',
      'stockist-summary',
    ]) {
      void qc.invalidateQueries({ queryKey: [k] });
    }
  };

  // ── Receive payment ──
  const [payOpen, setPayOpen] = useState(false);
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState<string>('cash');
  const [reference, setReference] = useState('');
  const [fundsAccountId, setFundsAccountId] = useState('');
  const outstanding = soaQ.data?.closingBalance ?? '0';

  const pay = useMutation({
    mutationFn: () =>
      createStockistReceipt({
        stockistBranchId: stockistBranchId!,
        ...(isScoped ? {} : { branchId: tillBranchId ?? undefined }),
        amount,
        method,
        reference: reference.trim() || null,
        fundsAccountId: fundsAccountId || null,
        // No `allocations`: the server applies oldest open billing first, which
        // is what a cashier taking a lump payment means.
      }),
    onSuccess: (r) => {
      toast.success(`Payment ${r.docNo} recorded.`);
      setPayOpen(false);
      setAmount('');
      setReference('');
      refresh();
    },
    onError: (e) => toast.error(apiError(e)),
  });

  // ── Return unsold stock ──
  const [returnQty, setReturnQty] = useState<Record<string, number>>({});
  const returnLines = useMemo(
    () =>
      Object.entries(returnQty)
        .filter(([, qty]) => qty > 0)
        .map(([key, qty]) => {
          const [productId, variantId] = key.split('|');
          return { productId: productId!, variantId: variantId || null, qty };
        }),
    [returnQty],
  );
  const doReturn = useMutation({
    mutationFn: () =>
      createStockistReturn({
        stockistBranchId: stockistBranchId!,
        ...(isScoped ? {} : { branchId: tillBranchId ?? undefined }),
        lines: returnLines,
      }),
    onSuccess: (r) => {
      toast.success(`Return ${r.docNo} recorded.`);
      setReturnQty({});
      refresh();
    },
    onError: (e) => toast.error(apiError(e)),
  });

  const voidReceipt = useMutation({
    mutationFn: (id: string) => {
      const reason = window.prompt('Reason for voiding this payment?')?.trim();
      if (!reason) throw new Error('cancelled');
      return voidStockistDoc('receipt', id, reason);
    },
    onSuccess: (r) => {
      toast.success(`${r.docNo} voided.`);
      refresh();
    },
    onError: (e) => {
      if (e instanceof Error && e.message === 'cancelled') return;
      toast.error(apiError(e));
    },
  });

  if (soaQ.isLoading) return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (!soaQ.data) return <p className="text-sm text-muted-foreground">Stockist not found.</p>;

  const holdings = holdingsQ.data ?? [];
  const invoices = invoicesQ.data ?? [];
  const receipts = receiptsQ.data ?? [];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">{soaQ.data.branch.name}</h1>
          <p className="text-sm text-muted-foreground">
            {soaQ.data.branch.code} · outstanding{' '}
            <span className="font-medium text-foreground">{peso(outstanding)}</span>
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate('/stockist-consignment')}>
            Back
          </Button>
          {canPrint && (
            <Button variant="outline" onClick={() => printStockistSoa(soaQ.data!)}>
              Print Statement
            </Button>
          )}
          {canReceive && Number(outstanding) > 0 && (
            <Button onClick={() => setPayOpen((v) => !v)}>Receive Payment</Button>
          )}
        </div>
      </div>

      {payOpen && canReceive && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Receive Payment</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Applied to the oldest open billing first. Outstanding: {peso(outstanding)}.
            </p>
            <div className="flex flex-wrap gap-3">
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">Amount</label>
                <Input
                  inputMode="decimal"
                  value={amount}
                  className="w-36"
                  onChange={(e) => setAmount(e.target.value.replace(/[^0-9.]/g, ''))}
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">Method</label>
                <select
                  className="h-9 w-36 rounded-md border bg-background px-2 text-sm"
                  value={method}
                  onChange={(e) => setMethod(e.target.value)}
                >
                  {ACCOUNTING_PAYMENT_METHODS.map((m) => (
                    <option key={m} value={m}>
                      {m.replace('_', ' ')}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">Reference</label>
                <Input
                  value={reference}
                  className="w-44"
                  onChange={(e) => setReference(e.target.value)}
                  placeholder="Cheque / txn no."
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">
                  Received into {fundsQ.data?.required ? '(required)' : '(optional)'}
                </label>
                <select
                  className="h-9 w-52 rounded-md border bg-background px-2 text-sm"
                  value={fundsAccountId}
                  onChange={(e) => setFundsAccountId(e.target.value)}
                >
                  <option value="">—</option>
                  {(fundsQ.data?.rows ?? []).map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                disabled={!amount || Number(amount) <= 0 || pay.isPending}
                onClick={() => pay.mutate()}
              >
                {pay.isPending ? 'Saving…' : 'Record Payment'}
              </Button>
              <Button variant="ghost" onClick={() => setPayOpen(false)}>
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Stock in their hands</CardTitle>
        </CardHeader>
        <CardContent>
          {holdings.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">
              Nothing consigned right now.
            </p>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                      <th className="py-2 pr-3">Product</th>
                      <th className="py-2 pr-3 text-right">Held</th>
                      {canReturn && <th className="py-2 pr-3">Return qty</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {holdings.map((h) => {
                      const key = `${h.productId}|${h.variantId ?? ''}`;
                      return (
                        <tr key={key} className="border-b last:border-0">
                          <td className="py-2 pr-3">
                            <span className="font-medium">{h.productName}</span>
                            {h.variantName ? (
                              <span className="text-muted-foreground"> · {h.variantName}</span>
                            ) : null}
                          </td>
                          <td className="py-2 pr-3 text-right">{h.qty}</td>
                          {canReturn && (
                            <td className="py-2 pr-3">
                              <Input
                                type="number"
                                min={0}
                                max={h.qty}
                                value={returnQty[key] ?? 0}
                                className="w-20"
                                onChange={(e) =>
                                  setReturnQty((prev) => ({
                                    ...prev,
                                    [key]: Math.max(
                                      0,
                                      Math.min(h.qty, Number(e.target.value) || 0),
                                    ),
                                  }))
                                }
                              />
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {canReturn && returnLines.length > 0 && (
                <div className="mt-3 flex items-center gap-2">
                  <Button disabled={doReturn.isPending} onClick={() => doReturn.mutate()}>
                    {doReturn.isPending
                      ? 'Saving…'
                      : `Take back ${returnLines.reduce((s, l) => s + l.qty, 0)} unit(s)`}
                  </Button>
                  <Button variant="ghost" onClick={() => setReturnQty({})}>
                    Clear
                  </Button>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Statement of account</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="py-2 pr-3">Date</th>
                  <th className="py-2 pr-3">Document</th>
                  <th className="py-2 pr-3">Ref</th>
                  <th className="py-2 pr-3 text-right">Charges</th>
                  <th className="py-2 pr-3 text-right">Payments</th>
                  <th className="py-2 text-right">Balance</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b text-muted-foreground">
                  <td className="py-2 pr-3" colSpan={5}>
                    Opening balance
                  </td>
                  <td className="py-2 text-right">{peso(soaQ.data.openingBalance)}</td>
                </tr>
                {soaQ.data.lines.map((l) => (
                  <tr key={`${l.docNo}-${l.date}`} className="border-b last:border-0">
                    <td className="py-2 pr-3">{new Date(l.date).toLocaleDateString()}</td>
                    <td className="py-2 pr-3">
                      <span className="font-mono text-xs">{l.docNo}</span>{' '}
                      <span className="text-muted-foreground">{l.docType}</span>
                    </td>
                    <td className="py-2 pr-3 text-muted-foreground">{l.ref ?? '—'}</td>
                    <td className="py-2 pr-3 text-right">
                      {Number(l.charges) ? peso(l.charges) : '—'}
                    </td>
                    <td className="py-2 pr-3 text-right">
                      {Number(l.payments) ? peso(l.payments) : '—'}
                    </td>
                    <td className="py-2 text-right font-medium">{peso(l.balance)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-3 text-sm">
            Charges {peso(soaQ.data.totalCharges)} · Payments {peso(soaQ.data.totalPayments)} ·{' '}
            <span className="font-medium">Closing {peso(soaQ.data.closingBalance)}</span>
          </p>
        </CardContent>
      </Card>

      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Billings</CardTitle>
          </CardHeader>
          <CardContent>
            {invoices.length === 0 ? (
              <p className="py-4 text-sm text-muted-foreground">None yet.</p>
            ) : (
              <table className="w-full text-sm">
                <tbody>
                  {invoices.map((i) => (
                    <tr key={i.id} className="border-b last:border-0">
                      <td className="py-2 pr-3 font-mono text-xs">{i.docNo}</td>
                      <td className="py-2 pr-3 text-muted-foreground">{i.status}</td>
                      <td className="py-2 text-right">
                        {peso(i.total)}
                        {Number(i.amountPaid) > 0 && (
                          <span className="text-muted-foreground">
                            {' '}
                            · paid {peso(i.amountPaid)}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Payments</CardTitle>
          </CardHeader>
          <CardContent>
            {receipts.length === 0 ? (
              <p className="py-4 text-sm text-muted-foreground">None yet.</p>
            ) : (
              <table className="w-full text-sm">
                <tbody>
                  {receipts.map((r) => (
                    <tr key={r.id} className="border-b last:border-0">
                      <td className="py-2 pr-3 font-mono text-xs">{r.docNo}</td>
                      <td className="py-2 pr-3 text-muted-foreground">
                        {r.status === 'void' ? 'void' : r.method.replace('_', ' ')}
                      </td>
                      <td className="py-2 pr-3 text-right">{peso(r.amount)}</td>
                      <td className="py-2 text-right">
                        {canVoid && r.status === 'posted' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => voidReceipt.mutate(r.id)}
                          >
                            Void
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
